// 剣舞の役者の速い連・左右交互に切りつける止め絵12枚を、ゲームの拍で動かした模型（2026-11-04）。
//   作者（11-04 answers/blade-quick-cross-keys4-1104）：stills=ok。原文「絵で出されるとよくわからない」。
//   並び＝右切り下げ→左切り下げ→右斬り上げ→左斬り上げ。前へ踏み出しながら、次の刀は少し引いて溜めてから弧で振る。
//   姿勢は blade_quick_cross.ts の12枚そのもの。間は crossBetween（手は弧 via・剣は手より遅れて返る）でつなぐ。
//   拍は配布版の速い連（blade_dancer_arle.ts BLADE_QUICK_TIMELINE）と同じ：溜め 0.70秒・当たり 0.12秒・1打ごとの間 0.30秒・戻り 0.70秒。
//   1打の中の割り振り（1打の間 S に対する割合・当たり t が基準）：
//     振りかぶり＝前の斬り抜けから t−0.70S まで（S=300 で 51ms）・溜め＝t−0.52S までに引き（54ms）、t−0.22S まで止める（90ms）・
//     斬り抜け＝t＋0.13S まで（105ms・中ほどまで加速し、当たりの後は減速して振り抜く）。
//     ③④の振りかぶりの1枚（振り抜いた低い手）は斬り抜けとほぼ同じ所なので通らず、斬り抜けからそのまま引く（④で剣先が前→後ろと揺れて見えた・11-04 チェック2回目）。
//   1発目の振りかぶりは合図を兼ね、溜めの長さ（0.70秒）の間そのまま見せる。4発目の後は振り抜いた形で止まり（決め）、立ち姿へ戻る。
//   斬り上げ（③④）の溜めは2案（11-04 チェック役4回で「引いたと読めない」が残り、作者に聞く）：
//     A＝剣先が腰の後ろへ回るくらい大きく引く（止め絵の「少し」40° を超える）／B＝剣は下のまま、体を深く沈めて一瞬止める。

import { BLADE_QUICK_CROSS_KEYS as K, crossBetween, crossKeyPose, type CrossKey } from './blade_quick_cross'
import type { MockupMove, Pose } from '../core'

/** 配布版の速い連の拍（blade_dancer_arle.ts：QUICK・CHAIN_GAP＋当たり＋1コマ＝300） */
export const BLADE_QUICK_CROSS_BEAT = { chargeMs: 700, activeMs: 120, recoveryMs: 700, stepMs: 300, hits: 4 }
/** 1打の中の割り振り（当たり t からのずれ・1打の間 S に対する割合） */
export const CROSS_PHASE = { wind: -0.70, pullIn: -0.52, pull: -0.22, cut: 0.13 }
const LEAD = 400
const SIGNAL_UP = 280   // 構えから合図（1発目の振りかぶり）へ上がる長さ
const DECIDE_HOLD = 260 // 4発目を振り抜いた形で止まる長さ
const TAIL = 300

const crossRest = (px: number, fF: number, fB: number, sOff: number): CrossKey => ({
  label: '構え', hit: 0, phase: 'wind', side: 'F', dir: 'down',
  body: { px, py: -73, lean: 3, tw: 0, ht: 0, fF: [fF, 0], fB: [fB, 0], rF: 0, rB: 0 },
  F: { hand: [8, 36], s: 60 + sOff, bend: -1 }, B: { hand: [2, 36], s: 70 + sOff, bend: -1 },
})
/** 始めの構え（足は止め絵の始めと同じ所）。終わりの構えは4発目で着いた所で後ろ足を引き寄せ、剣は前を通って下ろす（＋360） */
const REST0 = crossRest(1, 6, -4, 0)
const REST1 = crossRest(140, 128, 148, 360)
/** 戻りの途中：後ろ足を床から上げて運ぶ（着いたまま滑らせない）。両手は体の前で胸の下へ下ろし、剣は斜め前下（前へ突き出して見せない・11-04 チェック2回目「5回目の斬りに見える」） */
const REST_STEP: CrossKey = { ...REST1, body: { ...REST1.body, px: 136, py: -75, fF: [122, -6], rF: 0.8 },
  F: { hand: [8, 8], s: 395, bend: -1 }, B: { hand: [2, 8], s: 400, bend: -1 } }

export type UpPull = 'A' | 'B'
/** 斬り上げ（③④）の溜めの2案。止め絵の③④の溜め（K[7]・K[10]）に重ねる */
export const UP_PULL: Record<UpPull, { 3: CrossKey; 4: CrossKey }> = {
  A: {
    3: { ...K[7]!, F: { hand: [-14, 4], s: 492, bend: -1 } },
    4: { ...K[10]!, B: { hand: [-18, 2], s: 498, bend: -1 } },
  },
  B: {
    // 剣先は床から 20 ほど上に留める（沈めて剣を真下へ垂らすと剣先が床をこする・11-04 チェック5回目）
    3: { ...K[7]!, body: { ...K[7]!.body, py: -58, lean: 20 }, F: { hand: [8, 18], s: 422, bend: -1 } },
    4: { ...K[10]!, body: { ...K[10]!.body, py: -58, lean: 20 }, B: { hand: [0, 16], s: 428, bend: -1 } },
  },
}

type Seg = { at: number; key: CrossKey; ease: (u: number) => number }
const io = (u: number) => u < 0.5 ? 2 * u * u : 1 - Math.pow(-2 * u + 2, 2) / 2
const out = (u: number) => 1 - Math.pow(1 - u, 2)

export function makeBladeQuickCrossMotion(stepMs = BLADE_QUICK_CROSS_BEAT.stepMs, up: UpPull = 'B'): MockupMove {
  const B = BLADE_QUICK_CROSS_BEAT, S = stepMs
  const T = (i: number) => LEAD + B.chargeMs + i * S
  const segs: Seg[] = [{ at: 0, key: REST0, ease: io }, { at: LEAD, key: REST0, ease: io }, { at: LEAD + SIGNAL_UP, key: K[0]!, ease: io }]
  for (let i = 0; i < B.hits; i++) {
    const t = T(i), w = K[i * 3]!, p = i >= 2 ? UP_PULL[up][(i + 1) as 3 | 4] : K[i * 3 + 1]!, c = K[i * 3 + 2]!
    if (i === 0) segs.push({ at: t + CROSS_PHASE.pullIn * S - 130, key: w, ease: io })  // 合図：振りかぶりで止めて見せる
    else if (w.dir === 'down') segs.push({ at: t + CROSS_PHASE.wind * S, key: w, ease: io })
    segs.push({ at: t + CROSS_PHASE.pullIn * S, key: p, ease: out }, { at: t + CROSS_PHASE.pull * S, key: p, ease: io })  // 溜め：引いて、数コマ止める（11-04 模型のチェック）
    segs.push({ at: t + CROSS_PHASE.cut * S, key: c, ease: io })  // 斬り抜け：中ほどまで加速し、当たり（u≈0.64）の後は減速して振り抜く
  }
  const cutEnd = T(B.hits - 1) + CROSS_PHASE.cut * S, settleAt = cutEnd + DECIDE_HOLD
  const restAt = T(B.hits - 1) + B.activeMs + B.recoveryMs
  segs.push({ at: settleAt, key: K[11]!, ease: io }, { at: (settleAt + restAt) / 2, key: REST_STEP, ease: io }, { at: restAt, key: REST1, ease: io })
  const durMs = restAt + TAIL

  const poseFn = (t: number): Pose => {
    if (t <= segs[0]!.at) return crossKeyPose(segs[0]!.key)
    for (let j = 1; j < segs.length; j++) {
      const b = segs[j]!
      if (t > b.at) continue
      const a = segs[j - 1]!, u = b.at === a.at ? 1 : (t - a.at) / (b.at - a.at)
      return crossBetween(a.key, b.key, b.ease(u))
    }
    return crossKeyPose(segs[segs.length - 1]!.key)
  }
  const keyAt = segs.map(s => s.at)
  const sec = (S / 1000).toFixed(2), t0 = T(0), lastEnd = T(B.hits - 1) + B.activeMs
  return {
    id: `blade-quick-cross-${S}-${up}`, boss: '剣舞の役者', label: `速い連・左右交互に切りつける（1打 ${sec}秒・斬り上げの溜め ${up === 'A' ? 'A 大きく引く' : 'B 沈んで止める'}）`, body: 'human-dual-dress',
    durMs, scale: 0.8, baseX: 60, heroAt: 250, faceRight: true,
    keys: keyAt.map(at => ({ at, pose: poseFn(at) })), poseFn,
    phases: [
      { from: 0, to: LEAD, label: '構え', kind: 'rest' },
      { from: LEAD, to: t0 + CROSS_PHASE.pull * S, label: '合図：右の剣を頭の後ろへ振りかぶって止める', kind: 'tell' },
      { from: t0 + CROSS_PHASE.pull * S, to: t0, label: '溜め：少し引いてから振る', kind: 'tell' },
      { from: t0, to: lastEnd, label: `右切り下げ・左切り下げ・右斬り上げ・左斬り上げ（前へ踏み出しながら・${sec}秒ごと）`, kind: 'hit' },
      { from: lastEnd, to: settleAt, label: '決め：振り上げた形で止まる', kind: 'rest' },
      { from: settleAt, to: durMs, label: '戻り：後ろ足を引き寄せて構え', kind: 'rest' },
    ],
    hits: Array.from({ length: B.hits }, (_, i) => T(i)), activeMs: B.activeMs,
    box: [22, 100, -150, -40], paths: ['sF', 'sB'], fadeOutMs: 300,
    source: `溜め 0.70秒・当たり 0.12秒・1打ごとの間 ${sec}秒・戻り 0.70秒${S === B.stepMs ? '（配布版の速い連と同じ）' : `（1打の間だけ配布版 0.30秒から変えた案）`}`,
  }
}

export const BLADE_QUICK_CROSS_MOTION = makeBladeQuickCrossMotion()
export const BLADE_QUICK_CROSS_MOTION_A = makeBladeQuickCrossMotion(BLADE_QUICK_CROSS_BEAT.stepMs, 'A')
